import React, { useState, useEffect, useRef } from "react";
import { Client } from "@twilio/conversations";
import axios from "axios";
import Api from "../Api/Api.json";
import {
  CheckCheck,
  Loader,
} from "lucide-react";

const Chat = () => {
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState("");
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const [token, setToken] = useState("");
  const [conversationSID, setConversationSID] = useState("");

  // Get user info from localStorage
  const getUserInfo = () => {
    let userId = 0;
    let userName = "";

    if (localStorage.getItem("user_info") !== null) {
      const userInfo = JSON.parse(localStorage.getItem("user_info"));
      userId = userInfo.di_id;
      userName = userInfo.name;
    }

    return { userId, userName };
  };

  // Get chat token
  const getChatToken = async () => {
    const { userId, userName } = getUserInfo();

    const headers = { headers: { "Content-Type": "application/json" } };
    const url = Api.FITNESS_API;
    const requestData = {
      task: "getConversationCredForPatient",
      patient_id: userId,
      name: userName,
      timezone: "Asia/Dhaka",
    };
    try {
      const result = await axios.post(url, requestData, headers);
      if (result.data) {
        setToken(result.data.body);
        setConversationSID(result.data.conv_sid);
      }
    } catch (err) {
      console.error("Error getting chat token:", err);
      setError("Failed to connect to chat service.");
    }
  };

  // Initialize on page load
  useEffect(() => {
    getChatToken();
  }, []);

  // Initialize client on token change
  useEffect(() => {
    const initializeChat = async () => {
      if (!token || !conversationSID) return;

      try {
        // Initialize Twilio Conversations client
        const client = new Client(token);

        client.on("initialized", () => {
          console.log("Client initialized successfully");
        });

        client.on("initFailed", ({ error }) => {
          console.error("Client initialization failed:", error);
          setError("Failed to initialize chat client.");
        });

        // Get conversation by SID
        const conversation = await client.getConversationBySid(conversationSID);
        setConversation(conversation);
        setLoading(false);

        // Fetch initial messages
        const fetchedMessages = await conversation.getMessages(20);
        setMessages(fetchedMessages.items);

        // Listen for new messages
        conversation.on("messageAdded", (message) => {
          setMessages((prevMessages) => [...prevMessages, message]);
        });
      } catch (err) {
        console.error("Error initializing chat:", err);
        setError("Error connecting to chat service. Please try again later.");
      }
    };

    if (token) {
      initializeChat();
    }
  }, [token, conversationSID]);

  // Send message function
  const sendMessage = async () => {
    if (messageInput.trim() === "" || !conversation) return;

    try {
      await conversation.sendMessage(messageInput);
      setMessageInput("");
    } catch (err) {
      console.error("Error sending message:", err);
    }
  };

  // Handle key press for sending messages
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Auto scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Format date for message timestamps and group headers
  const formatMessageTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const formatDateHeader = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
      });
    }
  };

  // Group messages by date
  const groupMessagesByDate = () => {
    const groups = {};

    messages.forEach((message) => {
      const date = new Date(message.dateCreated).toDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(message);
    });

    return Object.entries(groups).map(([date, messages]) => ({
      date,
      dateFormatted: formatDateHeader(date),
      messages,
    }));
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-4rem)] bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-md flex flex-col items-center">
          <Loader className="w-10 h-10 text-teal-500 animate-spin" />
          <p className="mt-4 text-gray-700 font-medium">
            Connecting to chat service...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-4rem)] bg-gray-100">
        <div className="p-6 bg-white border border-red-100 rounded-lg shadow-md max-w-md w-full">
          <p className="text-red-600 font-medium mb-3">{error}</p>
          <button
            onClick={getChatToken}
            className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-md transition-colors font-medium"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  const { userName } = getUserInfo();
  const groupedMessages = groupMessagesByDate();

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] bg-[#f0f2f5] overflow-hidden">
      {/* Chat background with pattern */}
      <div
        className="flex-1 overflow-y-auto bg-[#e4ddd4] p-3 mb-auto flex flex-col justify-end"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%239C92AC' fill-opacity='0.05' fill-rule='evenodd'/%3E%3C/svg%3E")`,
          backgroundAttachment: "fixed",
        }}
      >
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-600">
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-lg shadow-md">
              <p className="text-center">
                No messages yet. Start the conversation!
              </p>
            </div>
          </div>
        ) : (
          <div className="mt-auto">
            {groupedMessages.map((group, groupIndex) => (
              <div key={groupIndex}>
                {/* Date header */}
                <div className="flex justify-center my-4">
                  <div className="bg-[#e1f3fb] px-3 py-1 rounded-lg text-xs text-gray-700 shadow-sm">
                    {group.dateFormatted}
                  </div>
                </div>

                {/* Messages for this day */}
                {group.messages.map((message, messageIndex) => {
                  const isSelf = message.author !== "Health Worker";
                  console.log(message.author);
                  const showAvatar =
                    !isSelf &&
                    (messageIndex === 0 ||
                      group.messages[messageIndex - 1].author !==
                        message.author);
                  const isLastInGroup =
                    messageIndex === group.messages.length - 1 ||
                    group.messages[messageIndex + 1].author !== message.author;

                  return (
                    <div
                      key={messageIndex}
                      className={`mb-1 flex ${
                        isSelf ? "justify-end" : "justify-start"
                      }`}
                    >
                      {!isSelf && showAvatar && (
                        <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 mt-1 mr-1 text-xs font-medium">
                          {message.author.charAt(0)}
                        </div>
                      )}

                      {!isSelf && !showAvatar && (
                        <div className="w-8 mr-1"></div>
                      )}

                      <div
                        className={`max-w-xs md:max-w-md px-3 py-2 rounded-lg ${
                          isSelf
                            ? "bg-[#d9fdd3] text-gray-800 rounded-tr-none"
                            : "bg-white text-gray-800 rounded-tl-none"
                        } ${isLastInGroup ? "mb-3" : ""}`}
                      >
                        {/* {!isSelf && showAvatar && (
                          <span className="text-xs font-medium text-[#53bdeb] block mb-1">
                            {message.author}
                          </span>
                        )} */}
                        <p className="break-words text-sm">{message.body}</p>
                        <div className="flex justify-end items-center -mb-1 mt-1">
                          <span className="text-[10px] text-gray-500 mr-1">
                            {formatMessageTime(message.dateCreated)}
                          </span>
                          {/* {isSelf && (
                            <CheckCheck size={14} className="text-[#53bdeb]" />
                          )} */}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input area */}
      <div className="bg-[#f0f2f5] px-3 py-2 border-t border-gray-200 mt-auto">
        <div className="flex items-center bg-white rounded-full px-3 py-1">
          <input
            type="text"
            value={messageInput}
            onChange={(e) => setMessageInput(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="Type a message"
            className="flex-1 py-2 px-2 bg-transparent outline-none text-gray-800"
          />
        </div>
      </div>
    </div>
  );
};

export default Chat;
